import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@interstate/components/Box';
import { InformationCircleIcon } from '@interstate/components/Icons';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import * as proposalSelectors from '@makemydeal/dr-dash-store/selectors/proposalSelectors';
import * as printPdfSelectors from '@makemydeal/dr-dash-store/selectors/printPdfSelectors';

import { PrintProposalPreview } from '../../printingProposals/PrintProposalPreview';
import { PrintScenarioSelector } from '../../printingProposals/PrintScenarioSelector';
import { usePrintScenarioSelection } from '../../printingProposals/usePrintScenarioSelection';
import { usePrintGridSelection } from '../../printingProposals/usePrintGridSelection';
import { LoadingSection } from '@makemydeal/dr-shared-components';
import type { ScopeGridSelections } from '@makemydeal/dr-dash-store/types/types';
import type { ToastType } from '../../common/DataLoadingToast/Toast';
import { Toast } from '../../common/DataLoadingToast/Toast';

export interface PreviewProposalHandle {
    getSelectedScopeIds: () => string[] | undefined;
    getScopeGridSelections: () => ScopeGridSelections | undefined;
}

interface PreviewProposalProps {
    isEditMode: boolean;
    isFourSquare?: boolean;
    closeDrawer: () => void;
    onActionsDisabled?: (disabled: boolean) => void;
}

export const getErrorMessage = (type: ToastType) => {
    if (type === 'error') {
        return 'Error printing proposal.';
    }
    return undefined;
};

export const PreviewProposal = forwardRef<PreviewProposalHandle, PreviewProposalProps>(
    ({ isEditMode, isFourSquare, closeDrawer, onActionsDisabled }, ref) => {
        const theme = useInterstateTheme();
        const createdProposal = useSelector(proposalSelectors.getCreatedProposal);
        const updatedProposal = useSelector(proposalSelectors.getUpdatedProposal);
        const isPrintLoading = useSelector(printPdfSelectors.isLoading);
        const isPrintSuccess = useSelector(printPdfSelectors.isSuccess);
        const isPrintError = useSelector(printPdfSelectors.isError);

        const {
            selectableScopeSummaries,
            selectedScopeIds,
            setSelectedScopeIds,
            showScenarioSelector: showSelector,
            getSelectedScopeIdsForAction
        } = usePrintScenarioSelection(isFourSquare);

        const { scopeGridInfos, scopeGridSelections, toggleTermIndex, toggleDownPaymentIndex } =
            usePrintGridSelection(selectedScopeIds);

        useImperativeHandle(
            ref,
            () => ({
                getSelectedScopeIds: getSelectedScopeIdsForAction,
                getScopeGridSelections: () => scopeGridSelections
            }),
            [getSelectedScopeIdsForAction, scopeGridSelections]
        );

        useEffect(() => {
            onActionsDisabled?.(showSelector && selectedScopeIds.length === 0);
        }, [showSelector, selectedScopeIds.length, onActionsDisabled]);

        useEffect(() => {
            if (isPrintSuccess) {
                closeDrawer();
            }
        }, [isPrintSuccess, closeDrawer]);

        if (!createdProposal && !updatedProposal) {
            return null;
        }

        //istanbul ignore next
        const { id: proposalId, title: proposalName } = isEditMode ? updatedProposal! : createdProposal!;

        return (
            <Box data-testid="preview-proposal-component" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <LoadingSection loading={isPrintLoading} />
                <Toast isLoading={isPrintLoading} isLoaded={isPrintSuccess} isError={isPrintError} getMessage={getErrorMessage} />
                <Box sx={{ mb: 2, mt: 2 }}>
                    <Typography variant="h3" data-testid="proposal-name-header">
                        {proposalName}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        mb: 2,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                    }}
                >
                    <InformationCircleIcon color={theme.tokens.BaseColorBlue500} />
                    <Typography variant="body-md" sx={{ paddingLeft: 1 }}>
                        New proposals are based on the default printing proposal
                    </Typography>
                </Box>
                {showSelector && (
                    <Box sx={{ mb: 2 }}>
                        <PrintScenarioSelector
                            scopeSummaries={selectableScopeSummaries}
                            selectedScopeIds={selectedScopeIds}
                            onSelectionChange={setSelectedScopeIds}
                            isFourSquare={isFourSquare}
                            scopeGridInfos={scopeGridInfos}
                            onToggleTermIndex={toggleTermIndex}
                            onToggleDownPaymentIndex={toggleDownPaymentIndex}
                        />
                    </Box>
                )}
                <PrintProposalPreview
                    proposalId={proposalId}
                    selectedScopeIds={getSelectedScopeIdsForAction()}
                    scopeGridSelections={scopeGridSelections}
                />
            </Box>
        );
    }
);

PreviewProposal.displayName = 'PreviewProposal';
